import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import BlogPostCard from '@/components/blog/BlogPostCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { API } from '@/App';
import { usePublicSeo, PUBLIC_SITE_URL } from '@/utils/seo';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  usePublicSeo({
    title: 'Blog Coli Noki | Livraison, e-commerce et logistique',
    description:
      'Découvrez les articles Coli Noki sur la logistique, le e-commerce, l\'expérience client et les bonnes pratiques de livraison.',
    path: '/blog',
    structuredData: [
      {
        id: 'blog',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          '@id': `${PUBLIC_SITE_URL}/blog#blog`,
          name: 'Blog Coli Noki',
          url: `${PUBLIC_SITE_URL}/blog`,
          inLanguage: 'fr',
          publisher: { '@id': `${PUBLIC_SITE_URL}/#organization` },
          description:
            'Articles Coli Noki sur la livraison, le suivi colis, le e-commerce et la gestion logistique digitale au Congo.',
        },
      },
    ],
  });

  useEffect(() => {
    let active = true;

    const loadPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts`);
        if (active) {
          setPosts(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (active) {
          setPosts([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPosts();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-red-700 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.18),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(239,68,68,0.18),transparent_38%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-5">
            <p className="text-sm font-semibold uppercase tracking-[0.26em] text-cyan-200">Blog</p>
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              Les insights Coli Noki pour la livraison et le commerce moderne
            </h1>
            <p className="text-lg leading-8 text-slate-200">
              Des articles pensés pour aider les vendeurs, les équipes logistiques et les marques à mieux expédier,
              mieux suivre et mieux servir leurs clients.
            </p>
            <Button asChild className="rounded-full bg-white text-red-700 hover:bg-white/90">
              <Link to="/services">Découvrir nos services</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Card key={`blog-skeleton-${index}`} className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
                  <div className="h-56 animate-pulse bg-slate-100" />
                  <CardContent className="space-y-4 p-6">
                    <div className="h-5 w-28 animate-pulse rounded-full bg-slate-100" />
                    <div className="h-8 w-full animate-pulse rounded-xl bg-slate-100" />
                    <div className="h-24 w-full animate-pulse rounded-2xl bg-slate-100" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <Card className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <CardContent className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">Blog</p>
                <h2 className="text-2xl font-semibold text-slate-950">Aucun article publie pour le moment</h2>
                <p className="max-w-2xl text-sm leading-7 text-slate-600">
                  Le blog Coli Noki sera bientot alimente avec des contenus logistiques, marketing et operationnels.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {posts.map((post) => (
                <BlogPostCard key={post.id || post.slug} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>

      <FooterNew />
    </div>
  );
};

export default BlogPage;
